import { useQuery } from '@tanstack/react-query'
import { getSubscription } from '../api/billing'

/**
 * Check if the current org's subscription plan includes a feature.
 *
 * Usage:
 *   const { hasFeature, features, isLoading } = useFeatureGate()
 *   if (hasFeature('analytics')) { ... }
 *
 * While loading, hasFeature returns true (permissive) to avoid hiding items
 * during initial load and then having them pop in.
 */
export function useFeatureGate() {
  const { data: subscription, isLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: getSubscription,
    staleTime: 60 * 1000,
  })

  const features: string[] = subscription?.planDetails?.features ?? []

  const hasFeature = (key: string): boolean => {
    // While loading, be permissive — don't hide nav items during initial load
    if (isLoading) return true
    // Billing-exempt organizations get all features
    if (subscription?.billingExempt) return true
    if (!subscription?.hasSubscription) return false
    return features.includes(key)
  }

  return {
    hasFeature,
    features,
    plan: subscription?.plan ?? null,
    hasSubscription: subscription?.hasSubscription ?? false,
    isLoading,
  }
}
